(() => {
  const MODE_LABELS = {
    single: 'Single Vision',
    progressive: 'Progressive',
    non_prescription: 'Non Prescription'
  };

  const RX_LABELS = {
    scan: 'Prescription scanned / photographed',
    manual: 'Entered manually',
    upload: 'Prescription uploaded',
    later: 'Send later',
    not_required: 'Not required'
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  class LensCustomizer {
    constructor(root) {
      this.root = root;
      this.instance = root.dataset.lcInstance || `${root.dataset.productId || 'product'}-${Date.now()}`;
      this.modal = root.querySelector('[data-lc-modal]');
      if (!this.modal || root.dataset.lcRxAvailable === 'false') return;

      this.catalog = this.parseScript('[data-lc-catalog-config]', {});
      this.brandConfig = this.parseScript('[data-lc-brand-config]', []);
      this.chargeMapsData = this.parseScript('[data-lc-charge-maps]', {});
      this.chargeMaps = new Map();
      Object.entries(this.chargeMapsData && typeof this.chargeMapsData === 'object' ? this.chargeMapsData : {}).forEach(([brandId, variants]) => {
        const map = new Map((Array.isArray(variants) ? variants : []).filter(v => v?.sku).map(v => [String(v.sku), v]));
        this.chargeMaps.set(String(brandId), map);
      });
      this.chargeMap = this.chargeMaps.get('essilor') || new Map();
      this.framePrice = Number(root.dataset.framePrice || 0);
      this.frameComparePrice = Number(root.dataset.frameComparePrice || this.framePrice);
      this.currency = root.dataset.currency || 'AED';
      this.currentStep = 'mode';
      this.rxView = 'methods';
      this.lastFocused = null;
      this.autoAdvanceTimer = null;
      this.isAdding = false;
      this.editBundleId = '';
      this.sunFilter = 'all';
      this.sunPolarizedOnly = false;
      this.state = {
        mode: '',
        brand: null,
        lensType: null,
        rxMethod: '',
        rx: {},
        treatment: null,
        color: null,
        thickness: null,
        bundleId: ''
      };

      this.portalModal();
      this.steps = {
        mode: this.modal.querySelector('[data-lc-step="mode"]'),
        brand: this.modal.querySelector('[data-lc-step="brand"]'),
        lens_type: this.modal.querySelector('[data-lc-step="lens_type"]'),
        prescription: this.modal.querySelector('[data-lc-step="prescription"]'),
        treatment: this.modal.querySelector('[data-lc-step="treatment"]'),
        thickness: this.modal.querySelector('[data-lc-step="thickness"]'),
        review: this.modal.querySelector('[data-lc-step="review"]')
      };
      this.nextButton = this.modal.querySelector('[data-lc-next]');
      this.headerBack = this.modal.querySelector('[data-lc-back]');
      this.progressBar = this.modal.querySelector('[data-lc-progress-bar]');
      this.progressTrack = this.modal.querySelector('.lc-panel__progress');
      this.validation = this.modal.querySelector('[data-lc-validation]');
      this.checkoutNote = this.modal.querySelector('[data-lc-checkout-note]');
      this.scrollContainer = this.modal.querySelector('[data-lc-scroll-container]');
      this.stage = this.modal.querySelector('[data-lc-stage]');
      this.stageMedia = this.modal.querySelector('[data-lc-stage-media]');
      this.productImage = this.modal.querySelector('[data-lc-product-image]');
      this.preview = this.modal.querySelector('[data-lc-transition-preview]');
      this.darknessInput = this.modal.querySelector('[data-lc-darkness]');
      this.virtualDemo = this.modal.querySelector('[data-lc-virtual-demo]');
      this.colorPreview = this.modal.querySelector('[data-lc-color-preview]');
      this.colorDemo = this.modal.querySelector('[data-lc-color-demo]');
      this.colorPreviewName = this.modal.querySelector('[data-lc-color-preview-name]');
      this.exitConfirm = this.modal.querySelector('[data-lc-exit-confirm]');
      this.entryBadge = this.root.querySelector('[data-lc-entry-badge]');
      this.entryBadgeFallback = this.root.dataset.lcPromoFallback || this.entryBadge?.textContent?.trim() || '';

      this.ensureProductForm();
      this.populatePrescriptionSelects();
      this.bindEvents();
      this.renderLensTypes();
      this.renderThickness();
      this.syncStep();
      this.updateSelectionSummary();
      this.updateMoneyDisplays();
      this.updateEntryPromotion();
      this.bindImageBackground();
      this.restoreFromUrl();
    }

    parseScript(selector, fallback) {
      const el = this.root.querySelector(selector);
      if (!el) return fallback;
      try {
        let parsed = JSON.parse(el.textContent.trim() || JSON.stringify(fallback));
        if (typeof parsed === 'string') parsed = JSON.parse(parsed);
        return parsed ?? fallback;
      } catch (error) {
        console.warn(`Lens customizer configuration could not be parsed: ${selector}`, error);
        return fallback;
      }
    }

    portalModal() {
      document.querySelectorAll('body > .lc-modal[data-lc-instance]').forEach(existing => {
        if (existing !== this.modal && existing.dataset.lcInstance === this.instance) existing.remove();
      });
      this.modal.dataset.lcPortaled = 'true';
      document.body.appendChild(this.modal);
    }

    ensureProductForm() {
      const form = this.productForm();
      if (!form) return;
      form.enctype = 'multipart/form-data';
      form.encoding = 'multipart/form-data';
    }

    productForm() {
      const formId = this.root.dataset.productFormId;
      return formId ? document.getElementById(formId) : null;
    }

    bindEvents() {
      this.root.querySelectorAll('[data-lc-open]').forEach(btn => btn.addEventListener('click', () => this.open()));
      this.modal.querySelector('[data-lc-close]')?.addEventListener('click', () => this.requestClose());
      this.headerBack?.addEventListener('click', () => this.back());
      this.nextButton?.addEventListener('click', () => this.next());

      const selectionsToggle = this.modal.querySelector('[data-lc-selections-toggle]');
      const selectionsPanel = this.modal.querySelector('[data-lc-selections-panel]');
      selectionsToggle?.addEventListener('click', () => {
        const expanded = selectionsToggle.getAttribute('aria-expanded') === 'true';
        selectionsToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (selectionsPanel) selectionsPanel.hidden = expanded;
      });
      this.modal.querySelector('[data-lc-selections-close]')?.addEventListener('click', () => {
        if (selectionsPanel) selectionsPanel.hidden = true;
        selectionsToggle?.setAttribute('aria-expanded', 'false');
      });
      this.modal.querySelectorAll('[data-lc-edit-step]').forEach(button => button.addEventListener('click', () => {
        this.editSelection(button.dataset.lcEditStep || '');
        if (selectionsPanel) selectionsPanel.hidden = true;
        selectionsToggle?.setAttribute('aria-expanded', 'false');
      }));
      this.modal.querySelector('[data-lc-exit-yes]')?.addEventListener('click', () => this.confirmExit());
      this.modal.querySelector('[data-lc-exit-no]')?.addEventListener('click', () => this.cancelExit());

      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || this.modal.hidden) return;
        if (this.exitConfirm && !this.exitConfirm.hidden) this.cancelExit();
        else this.requestClose();
      });

      this.modal.querySelectorAll('[data-lc-mode]').forEach(btn => btn.addEventListener('click', () => this.selectMode(btn)));
      this.modal.querySelectorAll('[data-lc-brand]').forEach(btn => btn.addEventListener('click', () => this.selectBrand(btn)));
      this.modal.querySelectorAll('[data-lc-rx-method]').forEach(btn => btn.addEventListener('click', () => this.selectRxMethod(btn)));

      this.modal.querySelector('[data-lc-dual-pd]')?.addEventListener('change', event => {
        this.modal.querySelector('[data-lc-single-pd]')?.toggleAttribute('hidden', event.target.checked);
        this.modal.querySelector('[data-lc-dual-pd-fields]')?.toggleAttribute('hidden', !event.target.checked);
        this.clearValidation();
      });

      this.modal.querySelector('[data-lc-scan]')?.addEventListener('change', event => {
        const file = event.target.files?.[0];
        const label = this.modal.querySelector('[data-lc-scan-filename]');
        if (label) label.textContent = file ? file.name : '';
        this.clearValidation();
        this.syncFooter();
      });
      this.modal.querySelector('[data-lc-upload]')?.addEventListener('change', event => {
        const file = event.target.files?.[0];
        const label = this.modal.querySelector('[data-lc-upload-filename]');
        if (label) label.textContent = file ? file.name : '';
        this.clearValidation();
        this.syncFooter();
      });

      this.darknessInput?.addEventListener('input', () => this.updateTransitionPreview());
      const productSection = this.root.closest('[data-section-type="product"]');
      productSection?.addEventListener('on:variant:change', event => {
        const variant = event.detail?.variant;
        if (!variant) return;
        this.framePrice = Number(variant.price || this.framePrice);
        this.frameComparePrice = Number(variant.compare_at_price || variant.compareAtPrice || this.framePrice);
        this.root.dataset.framePrice = String(this.framePrice);
        this.root.dataset.frameComparePrice = String(this.frameComparePrice);
        const nextImage = variant.featured_media?.preview_image?.src || variant.featured_image?.src || variant.featured_image;
        if (this.productImage && nextImage) {
          this.productImage.crossOrigin = 'anonymous';
          this.productImage.src = nextImage;
        }
        this.updateMoneyDisplays();
        this.updateEntryPromotion();
      });
    }

    bindImageBackground() {
      if (!this.productImage) return;
      const run = () => {
        if (this.stage) this.stage.style.backgroundColor = '#F2F2F2';
        if (this.stageMedia) this.stageMedia.style.backgroundColor = '#F2F2F2';
        this.productImage.style.filter = '';
        this.updateTransitionPreview();
        this.updateColorPreview();
      };
      if (this.productImage.complete) window.setTimeout(run, 0);
      this.productImage.addEventListener('load', run);
    }

    open() {
      this.lastFocused = document.activeElement;
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('lc-is-open');
      this.syncStep();
      window.setTimeout(() => this.modal.querySelector('[data-lc-close]')?.focus(), 0);
    }

    hasSelections() {
      return !!(this.state.mode || this.state.brand || this.state.lensType || this.state.rxMethod || this.state.treatment || this.state.color || this.state.thickness);
    }

    requestClose() {
      window.clearTimeout(this.autoAdvanceTimer);
      if (!this.hasSelections()) {
        this.close();
        return;
      }
      if (this.exitConfirm) {
        this.exitConfirm.hidden = false;
        this.modal.querySelector('[data-lc-exit-no]')?.focus();
      }
    }

    cancelExit() {
      if (this.exitConfirm) this.exitConfirm.hidden = true;
      this.modal.querySelector('[data-lc-close]')?.focus();
    }

    confirmExit() {
      this.resetConfiguration();
      if (this.exitConfirm) this.exitConfirm.hidden = true;
      this.close();
    }

    close() {
      window.clearTimeout(this.autoAdvanceTimer);
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('lc-is-open');
      this.lastFocused?.focus?.();
    }

    resetConfiguration() {
      this.state = { mode: '', brand: null, lensType: null, rxMethod: '', rx: {}, treatment: null, color: null, thickness: null, bundleId: '' };
      this.rxView = 'methods';
      this.currentStep = 'mode';
      this.modal.dataset.lcCurrentStep = 'mode';
      this.sunFilter = 'all';
      this.sunPolarizedOnly = false;
      this.markSelected('[data-lc-mode]', null);
      this.markSelected('[data-lc-brand]', null);
      this.markSelected('[data-lc-rx-method]', null);
      this.chargeMap = this.chargeMaps.get('essilor') || new Map();
      this.renderLensTypes();
      this.renderTreatment();
      this.renderThickness();
      this.showCheckoutError('');
      this.clearValidation();
      this.syncStep();
      this.updateSelectionSummary();
      this.updateMoneyDisplays();
      this.updateTransitionPreview();
      this.updateColorPreview();
    }

    editSelection(step) {
      if (!step) return;
      if (step === 'prescription') {
        if (this.state.mode === 'non_prescription') return this.goTo('mode');
        this.rxView = 'methods';
      }
      if (step === 'treatment' && !['transitions', 'sun'].includes(this.state.lensType?.kind || '')) return this.goTo('lens_type');
      this.goTo(step);
    }

    currentBrand() {
      return this.state.brand || null;
    }

    currentCatalog() {
      const base = this.catalog && typeof this.catalog === 'object' ? this.catalog : {};
      const brandId = this.state.brand?.id || '';
      const override = brandId && base.brands && typeof base.brands === 'object' ? base.brands[brandId] : null;
      return override && typeof override === 'object' ? { ...base, ...override } : base;
    }

    selectMode(btn) {
      window.clearTimeout(this.autoAdvanceTimer);
      this.state.mode = btn.dataset.lcMode || '';
      this.state.brand = null;
      this.state.lensType = null;
      this.state.treatment = null;
      this.state.color = null;
      this.state.thickness = null;
      this.state.rx = {};
      this.state.rxMethod = this.state.mode === 'non_prescription' ? 'not_required' : '';
      this.rxView = 'methods';
      this.chargeMap = this.chargeMaps.get('essilor') || new Map();
      this.markSelected('[data-lc-mode]', btn);
      this.markSelected('[data-lc-brand]', null);
      const addRow = this.modal.querySelector('[data-lc-add-row]');
      if (addRow) addRow.hidden = this.state.mode !== 'progressive';
      this.renderLensTypes();
      this.renderTreatment();
      this.renderThickness();
      this.updateSelectionSummary();
      this.updateMoneyDisplays();
      this.syncStagePreview();
      this.autoAdvanceTimer = window.setTimeout(() => this.goTo('brand'), 120);
    }

    selectBrand(btn) {
      window.clearTimeout(this.autoAdvanceTimer);
      const brandId = String(btn.dataset.lcBrand || '');
      const brands = Array.isArray(this.brandConfig) ? this.brandConfig : [];
      const brand = brands.find(item => String(item?.id || '') === brandId);
      if (!brand) return;
      this.state.brand = brand;
      this.state.lensType = null;
      this.state.treatment = null;
      this.state.color = null;
      this.state.thickness = null;
      this.chargeMap = this.chargeMaps.get(brandId) || new Map();
      this.markSelected('[data-lc-brand]', btn);
      this.renderLensTypes();
      this.renderTreatment();
      this.renderThickness();
      this.updateSelectionSummary();
      this.updateMoneyDisplays();
      this.updateEntryPromotion();
      this.syncStagePreview();
      this.autoAdvanceTimer = window.setTimeout(() => this.goTo('lens_type'), 140);
    }

    renderLensTypes() {
      const list = this.modal.querySelector('[data-lc-lens-type-list]');
      if (!list) return;
      list.innerHTML = '';
      const catalog = this.currentCatalog();
      const items = Array.isArray(catalog?.lens_types) ? catalog.lens_types : [];
      items.forEach((item, index) => {
        const price = this.componentPrice(item);
        const compare = this.componentComparePrice(item);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lc-commerce-card lc-lens-type-card';
        btn.dataset.lcLensType = item.id || `lens-${index}`;
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', this.state.lensType?.id === item.id ? 'true' : 'false');
        if (this.state.lensType?.id === item.id) btn.classList.add('is-selected');
        btn.innerHTML = `
          <span class="lc-commerce-card__visual" aria-hidden="true">${this.lensTypeIcon(item.kind || item.id)}</span>
          <span class="lc-commerce-card__body">
            ${item.badge ? `<span class="lc-commerce-card__top-badge">${this.escape(item.badge)}</span>` : ''}
            <strong class="lc-commerce-card__title">${this.escape(item.name || 'Lens type')}</strong>
            <span class="lc-commerce-card__description">${this.escape(item.description || '')}</span>
            <span class="lc-commerce-card__features">${this.featureList(item.features)}</span>
            <span class="lc-commerce-card__price-row">${this.priceMarkup(price, compare)}</span>
          </span>`;
        btn.addEventListener('click', () => this.selectLensType(btn, item));
        list.appendChild(btn);
      });
    }

    selectLensType(btn, item) {
      window.clearTimeout(this.autoAdvanceTimer);
      this.state.lensType = item;
      this.state.treatment = null;
      this.state.color = null;
      this.state.thickness = null;
      this.markSelected('[data-lc-lens-type]', btn);
      this.renderTreatment();
      this.renderThickness();
      this.updateSelectionSummary();
      this.updateMoneyDisplays();
      const next = this.state.mode === 'non_prescription' ? this.nextStepAfterPrescription() : 'prescription';
      this.autoAdvanceTimer = window.setTimeout(() => this.goTo(next), 140);
    }

    selectRxMethod(btn) {
      this.state.rxMethod = btn.dataset.lcRxMethod || '';
      this.markSelected('[data-lc-rx-method]', btn);
      this.clearValidation();
      if (this.state.rxMethod === 'later') {
        this.rxView = 'methods';
        this.updateSelectionSummary();
        window.setTimeout(() => this.goTo(this.nextStepAfterPrescription()), 120);
        return;
      }
      this.rxView = this.state.rxMethod;
      this.syncPrescriptionView();
      this.syncFooter();
      this.updateSelectionSummary();
    }

    nextStepAfterPrescription() {
      const kind = this.state.lensType?.kind || '';
      return (kind === 'transitions' || kind === 'sun') ? 'treatment' : 'thickness';
    }

    renderTreatment() {
      const host = this.modal.querySelector('[data-lc-treatment-content]');
      if (!host) return;
      host.innerHTML = '';
      const kind = this.state.lensType?.kind || '';
      if (kind === 'transitions') this.renderTransitions(host);
      if (kind === 'sun') this.renderSunColors(host);
    }

    renderTransitions(host) {
      const wrap = document.createElement('div');
      wrap.innerHTML = `<div class="lc-step__intro"><h2>CHOOSE YOUR TRANSITIONS® LENS TECHNOLOGY:</h2><p>Your glasses will come with best in class lens technology. Scratch-resistant treatment and UV protection are included.</p></div>`;
      const list = document.createElement('div');
      list.className = 'lc-treatment-list';
      const catalog = this.currentCatalog();
      const items = Array.isArray(catalog?.transitions_technologies) ? catalog.transitions_technologies : [];
      items.forEach((item, index) => {
        const current = this.componentPrice(item);
        const compare = this.componentComparePrice(item);
        const card = document.createElement('div');
        card.className = 'lc-commerce-card lc-treatment-card';
        card.tabIndex = 0;
        card.setAttribute('role', 'radio');
        card.setAttribute('aria-checked', this.state.treatment?.id === item.id ? 'true' : 'false');
        card.dataset.lcTreatment = item.id || `treatment-${index}`;
        if (this.state.treatment?.id === item.id) card.classList.add('is-selected');
        const colors = Array.isArray(item.colors) ? item.colors : [];
        card.innerHTML = `
          <span class="lc-commerce-card__visual" aria-hidden="true">${this.lensTypeIcon('transitions')}</span>
          <span class="lc-commerce-card__body">
            ${item.badge ? `<span class="lc-commerce-card__corner-badge${item.badge === 'NEW' ? ' lc-commerce-card__corner-badge--wide' : ''}">${this.escape(item.badge)}</span>` : ''}
            <strong class="lc-commerce-card__title">${this.escape(item.name || '')}</strong>
            <span class="lc-commerce-card__description">${this.escape(item.description || '')}</span>
            <span class="lc-commerce-card__features">${this.featureList(item.features)}</span>
            <span class="lc-color-inline"><b>Color:</b><span data-lc-inline-color-name>${this.escape(this.state.treatment?.id === item.id && this.state.color?.name ? this.state.color.name : (colors[0]?.name || ''))}</span></span>
            <span class="lc-inline-swatches">${colors.map((color, colorIndex) => `<button type="button" class="lc-inline-swatch${this.state.treatment?.id === item.id && this.state.color?.id === color.id ? ' is-selected' : ''}" data-lc-treatment-color="${this.escape(color.id)}" data-lc-treatment-index="${index}" data-lc-color-index="${colorIndex}" aria-label="${this.escape(color.name)}" style="--lc-swatch:${this.escape(color.hex || '#777')}"></button>`).join('')}</span>
            <span class="lc-commerce-card__price-row">${current > 0 ? this.priceMarkup(current, compare, true) : '<strong class="lc-included-price">Included</strong>'}</span>
          </span>`;
        const choose = () => {
          this.state.treatment = item;
          if (!this.state.color || !colors.some(c => c.id === this.state.color.id)) this.state.color = colors[0] || null;
          this.renderTreatment();
          this.updateSelectionSummary();
          this.updateMoneyDisplays();
          this.syncFooter();
          this.updateTransitionPreview();
        };
        card.addEventListener('click', event => {
          if (event.target.closest('[data-lc-treatment-color]')) return;
          choose();
        });
        card.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choose(); }
        });
        card.querySelectorAll('[data-lc-treatment-color]').forEach(swatch => {
          swatch.addEventListener('click', event => {
            event.stopPropagation();
            this.state.treatment = item;
            this.state.color = colors[Number(swatch.dataset.lcColorIndex || 0)] || colors[0] || null;
            this.renderTreatment();
            this.updateSelectionSummary();
            this.updateMoneyDisplays();
            this.syncFooter();
            this.updateTransitionPreview();
          });
        });
        list.appendChild(card);
      });
      wrap.appendChild(list);
      host.appendChild(wrap);
    }

    renderSunColors(host) {
      const wrap = document.createElement('div');
      wrap.innerHTML = `
        <div class="lc-step__intro lc-step__intro--compact">
          <h2>CHOOSE YOUR LENS COLOR</h2>
          <p>Enhanced color and contrast so you can see more detail.</p>
          <div class="lc-sun-benefits"><span>UV Protection ⓘ</span><span>Scratch-resistant treatment ⓘ</span><span>Anti-reflective Optimized ⓘ</span></div>
        </div>
        <div class="lc-sun-filters">
          <label class="lc-switch"><input type="checkbox" data-lc-polarized-only ${this.sunPolarizedOnly ? 'checked' : ''}><span></span><b>Polarized only</b></label>
          <div class="lc-filter-pills">
            <button type="button" data-lc-color-filter="all" class="${this.sunFilter === 'all' ? 'is-selected' : ''}">All</button>
            <button type="button" data-lc-color-filter="gradient" class="${this.sunFilter === 'gradient' ? 'is-selected' : ''}">Gradient</button>
            <button type="button" data-lc-color-filter="classic" class="${this.sunFilter === 'classic' ? 'is-selected' : ''}">Classic</button>
            <button type="button" data-lc-color-filter="mirror" class="${this.sunFilter === 'mirror' ? 'is-selected' : ''}">Mirror &amp; Flash</button>
          </div>
        </div>`;
      const grid = document.createElement('div');
      grid.className = 'lc-color-grid';
      const catalog = this.currentCatalog();
      const colors = (Array.isArray(catalog?.sun_colors) ? catalog.sun_colors : []).filter(color => {
        if (this.sunPolarizedOnly && !color.polarized) return false;
        return this.sunFilter === 'all' || color.category === this.sunFilter;
      });
      colors.forEach(color => {
        const display = this.sunColorDisplayPrice(color);
        const card = document.createElement('button');
        card.type = 'button';
        card.className = `lc-color-card${this.state.color?.id === color.id ? ' is-selected' : ''}`;
        card.dataset.lcSunColor = color.id;
        card.innerHTML = `
          <span class="lc-color-card__lens" style="--lc-color:${this.escape(color.hex || '#777')}"></span>
          <strong>${this.escape(color.name || '')}</strong>
          <small>${color.category === 'mirror' ? 'Mirror & Flash' : this.titleCase(color.category || 'Classic')}</small>
          <span class="lc-color-card__price">${this.priceMarkup(display.current, display.compare, true)}</span>`;
        card.addEventListener('click', () => {
          this.state.color = color;
          this.renderTreatment();
          this.updateSelectionSummary();
          this.updateMoneyDisplays();
          this.syncFooter();
          this.updateColorPreview();
        });
        grid.appendChild(card);
      });
      wrap.appendChild(grid);
      host.appendChild(wrap);
      wrap.querySelector('[data-lc-polarized-only]')?.addEventListener('change', event => {
        this.sunPolarizedOnly = event.target.checked;
        this.renderTreatment();
      });
      wrap.querySelectorAll('[data-lc-color-filter]').forEach(btn => btn.addEventListener('click', () => {
        this.sunFilter = btn.dataset.lcColorFilter || 'all';
        this.renderTreatment();
      }));
    }

    renderThickness() {
      const list = this.modal.querySelector('[data-lc-thickness-list]');
      if (!list) return;
      list.innerHTML = '';
      const catalog = this.currentCatalog();
      const items = Array.isArray(catalog?.thickness) ? catalog.thickness : [];
      items.forEach((item, index) => {
        const current = this.componentPrice(item);
        const compare = this.componentComparePrice(item);
        const card = document.createElement('button');
        card.type = 'button';
        card.className = `lc-commerce-card lc-thickness-card${this.state.thickness?.id === item.id ? ' is-selected' : ''}`;
        card.dataset.lcThickness = item.id || `thickness-${index}`;
        card.setAttribute('role', 'radio');
        card.setAttribute('aria-checked', this.state.thickness?.id === item.id ? 'true' : 'false');
        card.innerHTML = `
          <span class="lc-commerce-card__visual lc-thickness-card__visual" aria-hidden="true">${this.thicknessIcon(index)}</span>
          <span class="lc-commerce-card__body">
            <strong class="lc-commerce-card__title">${this.escape(item.name || '')}</strong>
            <span class="lc-commerce-card__description">${this.escape(item.description || '')}</span>
            <span class="lc-commerce-card__features">${this.featureList(item.features)}</span>
            <span class="lc-commerce-card__price-row">${current > 0 ? this.priceMarkup(current, compare, true) : '<strong class="lc-included-price">Included</strong>'}</span>
          </span>`;
        card.addEventListener('click', () => {
          this.state.thickness = item;
          this.renderThickness();
          this.updateSelectionSummary();
          this.updateMoneyDisplays();
          this.syncFooter();
        });
        list.appendChild(card);
      });
    }

    next() {
      this.clearValidation();
      if (this.currentStep === 'prescription') {
        if (this.rxView === 'manual') {
          if (!this.validateManualPrescription()) return;
          this.captureManualPrescription();
          this.goTo(this.nextStepAfterPrescription());
          return;
        }
        if (this.rxView === 'scan') {
          if (!this.modal.querySelector('[data-lc-scan]')?.files?.length) return this.showValidation('Please take or upload a clear prescription photo.');
          this.goTo(this.nextStepAfterPrescription());
          return;
        }
        if (this.rxView === 'upload') {
          if (!this.modal.querySelector('[data-lc-upload]')?.files?.length) return this.showValidation('Please choose a prescription file.');
          this.goTo(this.nextStepAfterPrescription());
          return;
        }
      }
      if (this.currentStep === 'treatment') {
        if (this.state.lensType?.kind === 'transitions' && !this.state.treatment) return this.showCheckoutError('Please choose a Transitions® lens technology.');
        if (this.state.lensType?.kind === 'sun' && !this.state.color) return this.showCheckoutError('Please choose a lens color.');
        this.goTo('thickness');
        return;
      }
      if (this.currentStep === 'thickness') {
        if (!this.state.thickness) return this.showCheckoutError('Please choose a lens thickness.');
        this.renderReview();
        this.goTo('review');
        return;
      }
      if (this.currentStep === 'review') this.addToCart();
    }

    back() {
      this.clearValidation();
      this.showCheckoutError('');
      if (this.currentStep === 'prescription' && this.rxView !== 'methods') {
        this.rxView = 'methods';
        this.syncPrescriptionView();
        this.syncFooter();
        return;
      }
      const sequence = this.sequence();
      const index = sequence.indexOf(this.currentStep);
      if (index > 0) this.goTo(sequence[index - 1]);
    }

    sequence() {
      const seq = ['mode', 'brand', 'lens_type'];
      if (this.state.mode !== 'non_prescription') seq.push('prescription');
      const kind = this.state.lensType?.kind || '';
      if (kind === 'transitions' || kind === 'sun') seq.push('treatment');
      seq.push('thickness', 'review');
      return seq;
    }

    goTo(step) {
      if (!this.steps[step]) return;
      this.currentStep = step;
      this.modal.dataset.lcCurrentStep = step;
      if (step === 'lens_type') this.renderLensTypes();
      if (step === 'treatment') this.renderTreatment();
      if (step === 'thickness') {
        if (!this.state.thickness) { const catalog = this.currentCatalog(); this.state.thickness = (Array.isArray(catalog?.thickness) ? catalog.thickness[0] : null) || null; }
        this.renderThickness();
      }
      if (step === 'review') this.renderReview();
      this.syncStep();
      this.updateSelectionSummary();
      this.updateMoneyDisplays();
      this.scrollContainer?.scrollTo({ top: 0, behavior: 'auto' });
    }

    syncStep() {
      Object.entries(this.steps).forEach(([name, element]) => { if (element) element.hidden = name !== this.currentStep; });
      const seq = this.sequence();
      const index = Math.max(0, seq.indexOf(this.currentStep));
      const progress = ((index + 1) / Math.max(1, seq.length)) * 100;
      if (this.progressBar) this.progressBar.style.width = `${progress}%`;
      if (this.progressTrack) {
        this.progressTrack.setAttribute('aria-valuemax', String(seq.length));
        this.progressTrack.setAttribute('aria-valuenow', String(index + 1));
      }
      if (this.headerBack) this.headerBack.hidden = index === 0;
      this.syncPrescriptionView();
      this.syncStagePreview();
      this.syncFooter();
    }

    syncPrescriptionView() {
      if (this.currentStep !== 'prescription') return;
      this.modal.querySelectorAll('[data-lc-rx-view]').forEach(view => { view.hidden = view.dataset.lcRxView !== this.rxView; });
    }

    syncFooter() {
      if (!this.nextButton) return;
      let show = false;
      let label = 'CONTINUE';
      if (this.currentStep === 'prescription') show = this.rxView !== 'methods';
      if (this.currentStep === 'treatment') {
        const kind = this.state.lensType?.kind;
        show = kind === 'transitions' ? !!this.state.treatment : kind === 'sun' ? !!this.state.color : false;
      }
      if (this.currentStep === 'thickness') show = !!this.state.thickness;
      if (this.currentStep === 'review') { show = true; label = this.isAdding ? 'ADDING…' : 'ADD TO BAG'; }
      this.nextButton.hidden = !show;
      this.nextButton.disabled = this.isAdding;
      this.nextButton.textContent = label;
    }

    syncStagePreview() {
      const kind = this.state.lensType?.kind || '';
      const previewSteps = ['treatment', 'thickness', 'review'];
      const showTransition = kind === 'transitions' && previewSteps.includes(this.currentStep);
      const showColor = kind === 'sun' && previewSteps.includes(this.currentStep);
      if (this.preview) this.preview.hidden = !showTransition;
      if (this.colorPreview) this.colorPreview.hidden = !showColor;
      this.updateTransitionPreview();
      this.updateColorPreview();
    }

    updateTransitionPreview() {
      if (this.productImage) this.productImage.style.filter = '';
      if (!this.virtualDemo) return;
      const active = this.preview && !this.preview.hidden && this.state.lensType?.kind === 'transitions' && !!this.state.treatment;
      const amount = active ? clamp(Number(this.darknessInput?.value || 35) / 100, 0, 1) : 0;
      const color = this.state.color?.hex || '#51575b';
      const treatmentId = String(this.state.treatment?.id || '');
      const indoorBase = treatmentId === 'xtractive' ? 0.10 : 0.025;
      const outdoorGain = treatmentId === 'xtractive' ? 0.68 : 0.58;
      const opacity = active ? clamp(indoorBase + amount * outdoorGain, 0, 0.82) : 0.02;
      this.virtualDemo.style.setProperty('--lc-transition-demo-color', color);
      this.virtualDemo.style.setProperty('--lc-transition-demo-opacity', opacity.toFixed(3));
      this.virtualDemo.dataset.lcPreviewState = amount >= .5 ? 'outdoor' : 'indoor';
    }

    updateColorPreview() {
      if (this.productImage) this.productImage.style.filter = '';
      if (!this.colorDemo) return;
      const selected = this.state.lensType?.kind === 'sun' ? this.state.color : null;
      this.colorDemo.style.setProperty('--lc-color-demo', selected?.hex || 'rgba(255,255,255,.42)');
      this.colorDemo.classList.toggle('is-empty', !selected);
      if (this.colorPreviewName) this.colorPreviewName.textContent = selected?.name || 'Select a lens color';
    }

    brandChargeSku(item) {
      if (!item?.charge_sku) return '';
      const raw = String(item.charge_sku);
      const prefix = String(this.state.brand?.sku_prefix || '');
      return prefix && !raw.startsWith(prefix) ? `${prefix}${raw}` : raw;
    }

    brandPriceMultiplier() {
      const multiplier = Number(this.state.brand?.price_multiplier || 1);
      return Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 1;
    }

    componentVariant(item) {
      const sku = this.brandChargeSku(item);
      if (!sku) return null;
      return this.chargeMap.get(sku) || null;
    }

    componentPrice(item) {
      const variant = this.componentVariant(item);
      if (variant && Number.isFinite(Number(variant.price))) return Number(variant.price);
      return Math.round(Number(item?.price || item?.charge_price || 0) * this.brandPriceMultiplier());
    }

    componentComparePrice(item) {
      const variant = this.componentVariant(item);
      if (variant) {
        const price = Number(variant.price || 0);
        const compare = Number(variant.compare_price || price);
        return Math.max(price, compare);
      }
      const multiplier = this.brandPriceMultiplier();
      const price = Math.round(Number(item?.price || item?.charge_price || 0) * multiplier);
      const compare = Math.round(Number(item?.compare_price || item?.charge_compare_price || item?.price || item?.charge_price || 0) * multiplier);
      return Math.max(price, compare);
    }

    sunColorPrice(color) {
      return this.componentPrice(color);
    }

    sunColorCompare(color) {
      return this.componentComparePrice(color);
    }

    sunColorDisplayPrice(color) {
      return { current: this.sunColorPrice(color), compare: this.sunColorCompare(color) };
    }

    lensPricing() {
      if (!this.state.lensType) return { current: 0, compare: 0 };
      let current = this.componentPrice(this.state.lensType);
      let compare = this.componentComparePrice(this.state.lensType);
      const kind = this.state.lensType.kind || '';
      if (kind === 'sun' && this.state.color) {
        current = this.sunColorPrice(this.state.color);
        compare = this.sunColorCompare(this.state.color);
      }
      if (kind === 'transitions' && this.state.treatment) {
        current += this.componentPrice(this.state.treatment);
        compare += this.componentComparePrice(this.state.treatment);
      }
      if (this.state.thickness) {
        current += this.componentPrice(this.state.thickness);
        compare += this.componentComparePrice(this.state.thickness);
      }
      return { current, compare: Math.max(current, compare) };
    }

    requiredCharges() {
      const charges = [];
      const add = (component, item, price, compare) => {
        if (!item || price <= 0) return;
        const variant = this.componentVariant(item);
        charges.push({ component, item, price, compare, variant, missing: !variant || !variant.id || variant.available === false });
      };
      const kind = this.state.lensType?.kind || '';
      if (this.state.lensType && !(kind === 'sun' && this.state.color)) add('Lens type', this.state.lensType, this.componentPrice(this.state.lensType), this.componentComparePrice(this.state.lensType));
      if (kind === 'transitions' && this.state.treatment) add('Lens treatment', this.state.treatment, this.componentPrice(this.state.treatment), this.componentComparePrice(this.state.treatment));
      if (kind === 'sun' && this.state.color) add('Lens color', this.state.color, this.sunColorPrice(this.state.color), this.sunColorCompare(this.state.color));
      if (this.state.thickness) add('Lens thickness', this.state.thickness, this.componentPrice(this.state.thickness), this.componentComparePrice(this.state.thickness));
      return charges;
    }

    renderReview() {
      const text = (selector, value) => this.modal.querySelectorAll(selector).forEach(el => { el.textContent = value || '—'; });
      text('[data-lc-review-mode]', MODE_LABELS[this.state.mode] || '—');
      text('[data-lc-review-brand]', this.state.brand?.name || '—');
      text('[data-lc-review-lens-type]', this.state.lensType?.name || '—');
      text('[data-lc-review-treatment]', this.state.treatment?.name || '—');
      text('[data-lc-review-color]', this.state.color?.name || '—');
      text('[data-lc-review-thickness]', this.state.thickness?.name || '—');
      text('[data-lc-review-rx]', RX_LABELS[this.state.rxMethod] || '—');
      const showTreatment = this.state.lensType?.kind === 'transitions';
      const showColor = ['transitions', 'sun'].includes(this.state.lensType?.kind || '');
      this.modal.querySelector('[data-lc-review-treatment-row]')?.toggleAttribute('hidden', !showTreatment);
      this.modal.querySelector('[data-lc-review-color-row]')?.toggleAttribute('hidden', !showColor);
      this.showCheckoutError('');
      this.applyProperties();
      this.updateMoneyDisplays();
    }

    updateSelectionSummary() {
      const set = (selector, value) => this.modal.querySelectorAll(selector).forEach(el => { el.textContent = value || 'Not selected'; });
      set('[data-lc-stage-mode]', MODE_LABELS[this.state.mode]);
      set('[data-lc-stage-brand]', this.state.brand?.name);
      set('[data-lc-stage-rx]', RX_LABELS[this.state.rxMethod]);
      set('[data-lc-stage-lens-type]', this.state.lensType?.name);
      set('[data-lc-stage-treatment]', this.state.treatment?.name);
      set('[data-lc-stage-color]', this.state.color?.name);
      set('[data-lc-stage-thickness]', this.state.thickness?.name);
      const kind = this.state.lensType?.kind || '';
      this.modal.querySelector('[data-lc-stage-mode-row]')?.toggleAttribute('hidden', !this.state.mode);
      this.modal.querySelector('[data-lc-stage-brand-row]')?.toggleAttribute('hidden', !this.state.brand);
      this.modal.querySelector('[data-lc-stage-lens-type-row]')?.toggleAttribute('hidden', !this.state.lensType);
      this.modal.querySelector('[data-lc-stage-treatment-row]')?.toggleAttribute('hidden', kind !== 'transitions' || !this.state.treatment);
      this.modal.querySelector('[data-lc-stage-color-row]')?.toggleAttribute('hidden', !['transitions', 'sun'].includes(kind) || !this.state.color);
      this.modal.querySelector('[data-lc-stage-thickness-row]')?.toggleAttribute('hidden', !this.state.thickness);
      this.modal.querySelector('[data-lc-stage-rx-row]')?.toggleAttribute('hidden', !this.state.rxMethod || this.state.mode === 'non_prescription');
      this.setSelectionPrice('[data-lc-stage-lens-type-price]', kind === 'sun' ? 0 : this.componentPrice(this.state.lensType), kind === 'sun' ? 0 : this.componentComparePrice(this.state.lensType));
      this.setSelectionPrice('[data-lc-stage-treatment-price]', kind === 'transitions' ? this.componentPrice(this.state.treatment) : 0, kind === 'transitions' ? this.componentComparePrice(this.state.treatment) : 0);
      this.setSelectionPrice('[data-lc-stage-color-price]', kind === 'sun' ? this.sunColorPrice(this.state.color) : 0, kind === 'sun' ? this.sunColorCompare(this.state.color) : 0);
      this.setSelectionPrice('[data-lc-stage-thickness-price]', this.componentPrice(this.state.thickness), this.componentComparePrice(this.state.thickness));
    }

    setSelectionPrice(selector, current, compare) {
      this.modal.querySelectorAll(selector).forEach(el => {
        current = Number(current || 0);
        compare = Math.max(current, Number(compare || current));
        el.innerHTML = current > 0 ? this.priceMarkup(current, compare, true) : (current === 0 && (selector.includes('treatment') || selector.includes('thickness')) ? '<strong>Included</strong>' : '');
      });
    }

    updateEntryPromotion() {
      if (!this.entryBadge) return;
      const catalog = this.currentCatalog();
      const pools = [catalog?.lens_types, catalog?.transitions_technologies, catalog?.sun_colors, catalog?.thickness];
      let maxPct = 0;
      pools.forEach(pool => (Array.isArray(pool) ? pool : []).forEach(item => {
        const current = this.componentPrice(item);
        const compare = this.componentComparePrice(item);
        if (current > 0 && compare > current) maxPct = Math.max(maxPct, Math.round((1 - current / compare) * 100));
      }));
      if (maxPct > 0) {
        this.entryBadge.textContent = `UP TO ${maxPct}% OFF`;
        this.entryBadge.hidden = false;
      } else if (this.entryBadgeFallback) {
        this.entryBadge.textContent = this.entryBadgeFallback;
        this.entryBadge.hidden = false;
      } else {
        this.entryBadge.hidden = true;
      }
    }

    updateMoneyDisplays() {
      const pricing = this.lensPricing();
      const frameCompare = Math.max(this.framePrice, this.frameComparePrice || this.framePrice);
      const total = this.framePrice + pricing.current;
      const totalCompare = frameCompare + pricing.compare;
      const lensSelected = !!this.state.lensType;
      const setText = (selector, value) => this.modal.querySelectorAll(selector).forEach(el => { el.textContent = value; });
      const setCompare = (selector, compare, current) => this.modal.querySelectorAll(selector).forEach(el => {
        el.textContent = compare > current ? this.money(compare) : '';
        el.hidden = !(compare > current);
      });

      setText('[data-lc-summary-frame]', this.money(this.framePrice));
      setText('[data-lc-summary-lens]', pricing.current > 0 ? this.money(pricing.current) : 'Included');
      setCompare('[data-lc-summary-lens-compare]', pricing.compare, pricing.current);
      setText('[data-lc-summary-total]', this.money(total));
      setCompare('[data-lc-summary-total-compare]', totalCompare, total);
      setText('[data-lc-footer-price-label]', lensSelected ? 'FRAME + LENSES PRICE:' : 'FRAME PRICE:');
      setText('[data-lc-footer-price]', this.money(lensSelected ? total : this.framePrice));
      setCompare('[data-lc-footer-compare]', lensSelected ? totalCompare : frameCompare, lensSelected ? total : this.framePrice);
    }

    priceMarkup(current, compare, compact = false) {
      current = Number(current || 0);
      compare = Math.max(current, Number(compare || current));
      const pct = compare > current ? Math.round((1 - current / compare) * 100) : 0;
      if (current <= 0) return '<strong class="lc-included-price">Included</strong>';
      return `${pct > 0 ? `<span class="lc-discount-badge">${pct}% OFF</span>` : ''}${compare > current ? `<del>${this.money(compare)}</del>` : ''}<strong>${compact ? '+' : ''}${this.money(current)}</strong>`;
    }

    featureList(features) {
      return (Array.isArray(features) ? features : []).map(feature => `<span><i aria-hidden="true">✓</i>${this.escape(feature)}</span>`).join('');
    }

    lensTypeIcon(kind) {
      const type = String(kind || '').toLowerCase();
      let fill = 'linear-gradient(135deg,#f7f7f7,#d6d6d6)';
      let extra = '';
      if (type === 'transitions') fill = 'linear-gradient(135deg,#aaa,#4d4545)';
      if (type === 'sun') fill = 'linear-gradient(135deg,#a3a3a3,#545454)';
      if (type === 'blue') { fill = 'linear-gradient(135deg,#f7fbff,#d8efff)'; extra = '<path d="M25 19l44 43M38 13l43 43" stroke="#58b9ff" stroke-width="4" opacity=".55"/>'; }
      return `<svg viewBox="0 0 110 92" aria-hidden="true"><defs><linearGradient id="g-${this.instance}-${type}" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${type === 'blue' ? '#f5fbff' : type === 'clear' ? '#fff' : '#b9b9b9'}"/><stop offset="1" stop-color="${type === 'transitions' ? '#514747' : type === 'sun' ? '#5b5b5b' : type === 'blue' ? '#d7ecff' : '#e9edf1'}"/></linearGradient></defs><path d="M15 16C27 5 74 5 93 18c11 8 11 45-3 56-14 11-52 12-68 1C9 66 5 29 15 16Z" fill="url(#g-${this.instance}-${type})" stroke="#9a9a9a"/>${extra}</svg>`;
    }

    thicknessIcon(index) {
      const count = index === 0 ? 2 : 3;
      return `<svg viewBox="0 0 105 80" aria-hidden="true">${Array.from({ length: count }).map((_, i) => `<path d="M${18 + i * 18} 15c7 4 9 45 0 51-8-6-8-46 0-51Z" fill="#f8f8f8" stroke="#d3d3d3"/><path d="M${25 + i * 18} 15c7 4 9 45 0 51" fill="none" stroke="#ef3829" stroke-width="3" opacity=".8"/>`).join('')}</svg>`;
    }

    applyProperties() {
      if (!this.state.bundleId) this.state.bundleId = `lc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const pricing = this.lensPricing();
      if (this.state.rxMethod === 'manual') this.captureManualPrescription();
      const values = {
        vision_need: MODE_LABELS[this.state.mode] || '',
        lens_brand: this.state.brand?.name || '',
        lens_type: this.state.lensType?.name || '',
        lens_treatment: this.state.treatment?.name || '',
        lens_color: this.state.color?.name || '',
        lens_thickness: this.state.thickness?.name || '',
        lens_price: pricing.current > 0 ? this.money(pricing.current) : 'Included',
        rx_method: RX_LABELS[this.state.rxMethod] || '',
        rx_name: this.state.rx.rx_name || '',
        od_sph: this.state.rx.od_sph || '', od_cyl: this.state.rx.od_cyl || '', od_axis: this.state.rx.od_axis || '', od_add: this.state.rx.od_add || '',
        os_sph: this.state.rx.os_sph || '', os_cyl: this.state.rx.os_cyl || '', os_axis: this.state.rx.os_axis || '', os_add: this.state.rx.os_add || '',
        pd: this.state.rx.pd || '', od_pd: this.state.rx.od_pd || '', os_pd: this.state.rx.os_pd || '',
        bundle_id: this.state.bundleId,
        mode_key: this.state.mode || '',
        brand_key: this.state.brand?.id || '',
        pricing_product: this.state.brand?.handle || '',
        lens_type_id: this.state.lensType?.id || '',
        treatment_id: this.state.treatment?.id || '',
        color_id: this.state.color?.id || '',
        thickness_id: this.state.thickness?.id || '',
        rx_method_key: this.state.rxMethod || '',
        expected_charge_minor: String(pricing.current || 0)
      };
      this.root.querySelectorAll('[data-lc-property]').forEach(input => {
        const value = values[input.dataset.lcProperty] ?? '';
        input.value = value;
        input.disabled = value === '';
      });
      const scan = this.modal.querySelector('[data-lc-scan]');
      const upload = this.modal.querySelector('[data-lc-upload]');
      if (scan) scan.disabled = !(this.state.rxMethod === 'scan' && scan.files?.length);
      if (upload) upload.disabled = !(this.state.rxMethod === 'upload' && upload.files?.length);
    }

    async ensureChargeMap() {
      const brand = this.currentBrand();
      if (!brand) return;
      const brandId = String(brand.id || '');
      let activeMap = this.chargeMaps.get(brandId);
      if (activeMap?.size) { this.chargeMap = activeMap; return; }
      activeMap = activeMap || new Map();
      this.chargeMaps.set(brandId, activeMap);
      this.chargeMap = activeMap;
      const rootUrl = window.Shopify?.routes?.root || '/';
      const handle = String(brand.handle || (brandId === 'zeiss' ? 'zeiss-lens' : 'essilor-lens'));
      try {
        const response = await fetch(`${rootUrl}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const product = await response.json();
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        variants.forEach(variant => {
          if (!variant?.sku) return;
          activeMap.set(String(variant.sku), {
            sku: variant.sku, id: variant.id, price: Number(variant.price || 0),
            compare_price: Number(variant.compare_at_price || variant.price || 0), available: variant.available !== false, title: variant.title || ''
          });
        });
        this.chargeMap = activeMap;
        this.updateEntryPromotion();
        this.renderLensTypes();
        this.renderTreatment();
        this.renderThickness();
        this.updateSelectionSummary();
        this.updateMoneyDisplays();
      } catch (error) {
        console.warn('Lens pricing product could not be loaded automatically.', error);
      }
    }

    async addToCart() {
      if (this.isAdding) return;
      if (!this.state.mode) return this.showCheckoutError('Please choose your vision need before adding lenses to the bag.');
      if (!this.state.brand) return this.showCheckoutError('Please choose a lens brand before adding lenses to the bag.');
      if (!this.state.lensType) return this.showCheckoutError('Please choose a lens type before adding lenses to the bag.');
      if (this.state.lensType?.kind === 'transitions' && !this.state.treatment) return this.showCheckoutError('Please choose a Transitions® lens technology before adding lenses to the bag.');
      if (this.state.lensType?.kind === 'sun' && !this.state.color) return this.showCheckoutError('Please choose a lens color before adding lenses to the bag.');
      if (!this.state.thickness) return this.showCheckoutError('Please choose a lens thickness before adding lenses to the bag.');
      const form = this.productForm();
      if (!form) return this.showCheckoutError('We could not prepare this product for checkout. Please refresh the page and try again.');
      await this.ensureChargeMap();
      const charges = this.requiredCharges();
      const missing = charges.filter(charge => charge.missing);
      if (missing.length) {
        console.error(`${this.state.brand?.name || 'Lens'} checkout price mapping is incomplete.`, missing);
        this.showCheckoutError('We couldn\'t confirm the price for one or more selected lens options. Please review your lens selections and try again. If the issue continues, contact our optical team.');
        return;
      }

      this.applyProperties();
      this.isAdding = true;
      this.syncFooter();
      this.showCheckoutError('');
      const rootUrl = window.Shopify?.routes?.root || '/';
      const endpoint = `${rootUrl}cart/add.js`;
      let frameItem = null;
      try {
        const formData = new FormData(form);
        const frameResponse = await fetch(endpoint, { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
        if (!frameResponse.ok) {
          const error = await frameResponse.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'This frame could not be added to the bag.');
        }
        frameItem = await frameResponse.json().catch(() => ({}));
        const quantity = Math.max(1, Number(formData.get('quantity') || 1));
        if (charges.length) {
          const lensResponse = await fetch(endpoint, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: charges.map(charge => ({
              id: Number(charge.variant.id),
              quantity,
              properties: {
                '_Lens Bundle ID': this.state.bundleId,
                '_Lens Charge': 'true',
                '_Lens Component': charge.component,
                '_Lens Parent Product': this.root.dataset.productTitle || '',
                '_Lens Mode Key': this.state.mode || '',
                '_Lens Brand': this.state.brand?.name || '',
                '_Lens Brand Key': this.state.brand?.id || '',
                '_Lens Pricing Product': this.state.brand?.handle || '',
                '_Lens Type ID': this.state.lensType?.id || '',
                '_Lens Expected Component Price Minor': String(charge.price || 0)
              }
            })) })
          });
          if (!lensResponse.ok) {
            const error = await lensResponse.json().catch(() => ({}));
            if (frameItem?.key) await this.removeCartKey(frameItem.key, rootUrl);
            throw new Error(error.description || error.message || 'The lens pricing components could not be added to the bag.');
          }
        }
        if (this.editBundleId) {
          await this.removeBundleFromCart(this.editBundleId, rootUrl);
          this.editBundleId = '';
        }
        window.location.assign(`${rootUrl}cart`);
      } catch (error) {
        console.error('Lens customizer add to cart error', error);
        this.isAdding = false;
        this.syncFooter();
        this.showCheckoutError(error.message || 'Something went wrong. Please try again.');
      }
    }

    async removeCartKey(key, rootUrl = '/') {
      return fetch(`${rootUrl}cart/change.js`, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity: 0 }) }).catch(() => null);
    }

    async removeBundleFromCart(bundleId, rootUrl = '/') {
      if (!bundleId) return;
      const response = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json().catch(() => null);
      const keys = (cart?.items || []).filter(item => String(item?.properties?.['_Lens Bundle ID'] || '') === String(bundleId)).map(item => item.key).filter(Boolean);
      for (const key of keys) await this.removeCartKey(key, rootUrl);
    }

    restoreFromUrl() {
      const params = new URLSearchParams(window.location.search);
      if (params.get('lens_customize') !== '1') return;
      this.editBundleId = params.get('lc_edit_bundle') || '';
      const mode = params.get('lc_mode') || '';
      if (MODE_LABELS[mode]) {
        this.state.mode = mode;
        this.state.rxMethod = mode === 'non_prescription' ? 'not_required' : (params.get('lc_rx_method') || 'later');
        const modeButton = this.modal.querySelector(`[data-lc-mode="${CSS.escape(mode)}"]`);
        this.markSelected('[data-lc-mode]', modeButton);
      }
      const brands = Array.isArray(this.brandConfig) ? this.brandConfig : [];
      const requestedBrand = params.get('lc_brand') || (mode ? 'essilor' : '');
      this.state.brand = brands.find(item => String(item?.id || '') === requestedBrand) || null;
      if (this.state.brand) {
        this.chargeMap = this.chargeMaps.get(String(this.state.brand.id)) || new Map();
        const brandButton = this.modal.querySelector(`[data-lc-brand="${CSS.escape(String(this.state.brand.id))}"]`);
        this.markSelected('[data-lc-brand]', brandButton);
      }
      const catalog = this.currentCatalog();
      const lensTypeId = params.get('lc_lens_type') || '';
      this.state.lensType = (catalog?.lens_types || []).find(item => String(item.id) === lensTypeId) || null;
      const treatmentId = params.get('lc_treatment') || '';
      this.state.treatment = (catalog?.transitions_technologies || []).find(item => String(item.id) === treatmentId) || null;
      const colorId = params.get('lc_color') || '';
      const colorPool = this.state.lensType?.kind === 'sun' ? (catalog?.sun_colors || []) : (this.state.treatment?.colors || []);
      this.state.color = colorPool.find(item => String(item.id) === colorId) || null;
      const thicknessId = params.get('lc_thickness') || '';
      this.state.thickness = (catalog?.thickness || []).find(item => String(item.id) === thicknessId) || null;
      this.renderLensTypes();
      this.renderTreatment();
      this.renderThickness();
      if (this.state.lensType && this.state.thickness) this.currentStep = 'review';
      else if (this.state.brand) this.currentStep = 'lens_type';
      else if (this.state.mode) this.currentStep = 'brand';
      else this.currentStep = 'mode';
      this.renderReview();
      this.syncStep();
      this.updateSelectionSummary();
      this.updateMoneyDisplays();
      this.updateEntryPromotion();
      window.setTimeout(() => this.open(), 0);
    }

    populatePrescriptionSelects() {
      const configs = {
        od_sph: [-20, 20, .25, true], os_sph: [-20, 20, .25, true],
        od_cyl: [-6, 6, .25, true], os_cyl: [-6, 6, .25, true],
        od_axis: [1, 180, 1, false], os_axis: [1, 180, 1, false],
        od_add: [.75, 4, .25, true], os_add: [.75, 4, .25, true],
        pd: [40, 80, .5, false], od_pd: [20, 40, .5, false], os_pd: [20, 40, .5, false]
      };
      this.modal.querySelectorAll('[data-lc-rx]').forEach(select => {
        const cfg = configs[select.dataset.lcRx];
        if (!cfg) return;
        const [min, max, step, signed] = cfg;
        select.innerHTML = '<option value="">--</option>';
        for (let value = min; value <= max + 0.0001; value += step) {
          const rounded = Math.round(value * 100) / 100;
          const decimals = step < 1 ? 2 : 0;
          const text = `${signed && rounded > 0 ? '+' : ''}${rounded.toFixed(decimals)}`;
          select.insertAdjacentHTML('beforeend', `<option value="${text}">${text}</option>`);
        }
      });
    }

    captureManualPrescription() {
      const rx = {};
      this.modal.querySelectorAll('[data-lc-rx]').forEach(select => { rx[select.dataset.lcRx] = select.value || ''; });
      rx.rx_name = this.modal.querySelector('[data-lc-rx-name]')?.value?.trim() || '';
      this.state.rx = rx;
    }

    validateManualPrescription() {
      const get = key => this.modal.querySelector(`[data-lc-rx="${key}"]`)?.value || '';
      if (!get('od_sph') || !get('os_sph')) return this.showValidation('Please select SPH for both right and left eyes.'), false;
      const cylKeys = ['od_cyl', 'os_cyl'];
      for (const key of cylKeys) {
        const cyl = get(key);
        const eye = key.startsWith('od') ? 'right' : 'left';
        if (cyl && Number(cyl) !== 0 && !get(key.replace('cyl', 'axis'))) return this.showValidation(`Please select AXIS for the ${eye} eye.`), false;
      }
      if (this.state.mode === 'progressive' && (!get('od_add') || !get('os_add'))) return this.showValidation('Progressive lenses require ADD values for both eyes.'), false;
      const dual = this.modal.querySelector('[data-lc-dual-pd]')?.checked;
      if (dual) {
        if (!get('od_pd') || !get('os_pd')) return this.showValidation('Please enter both right and left PD values.'), false;
      } else if (!get('pd')) return this.showValidation('Please enter your pupillary distance (PD).'), false;
      return true;
    }

    markSelected(selector, selected) {
      this.modal.querySelectorAll(selector).forEach(el => {
        const active = el === selected;
        el.classList.toggle('is-selected', active);
        if (el.hasAttribute('aria-checked')) el.setAttribute('aria-checked', active ? 'true' : 'false');
      });
    }

    showValidation(message) {
      if (!this.validation) return;
      this.validation.textContent = message || '';
      this.validation.hidden = !message;
      if (message) this.validation.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    clearValidation() { this.showValidation(''); }

    showCheckoutError(message) {
      if (!this.checkoutNote) return;
      delete this.checkoutNote.dataset.lcTechnical;
      this.checkoutNote.textContent = message || '';
      this.checkoutNote.hidden = !message;
      if (message) this.checkoutNote.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    money(minor) {
      const amount = Number(minor || 0) / 100;
      if (this.currency === 'AED') return `Dhs. ${amount.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED`;
      try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).format(amount); }
      catch (_) { return `${this.currency} ${amount.toFixed(2)}`; }
    }

    titleCase(value) { return String(value || '').replace(/(^|\s|[-_/])\w/g, c => c.toUpperCase()); }
    escape(value) {
      return String(value ?? '').replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));
    }
  }

  const init = scope => {
    (scope || document).querySelectorAll?.('[data-lens-customizer]').forEach(root => {
      if (root.dataset.lcInitialized === 'true') return;
      root.dataset.lcInitialized = 'true';
      new LensCustomizer(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();


/* Phase 8 universal wheel/touch scroll assist */
(() => {
  const bind = (modal) => {
    if (!modal || modal.dataset.lcScrollAssistBound === 'true') return;
    const body = modal.querySelector('.lc-panel__body');
    const panel = modal.querySelector('.lc-panel');
    if (!body || !panel) return;
    modal.dataset.lcScrollAssistBound = 'true';

    panel.addEventListener('wheel', (event) => {
      if (event.ctrlKey || Math.abs(event.deltaY) < 1) return;
      const target = event.target;
      if (target && (target.closest('input[type="range"]') || target.closest('select') || target.closest('textarea'))) return;
      const before = body.scrollTop;
      body.scrollTop += event.deltaY;
      if (body.scrollTop !== before) event.preventDefault();
    }, { passive: false });
  };

  const init = () => document.querySelectorAll('[data-lc-modal]').forEach(bind);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
